import React, { useEffect, useMemo, useState } from 'react';
import * as S from '../Advances/AdvancesStyles';
import { getUserCalendar, bulkMarkAttendance } from '../../../../services/attendanceService';
import { toast } from 'react-toastify';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Same palette Pill already uses elsewhere in Advances, reused so a "PRESENT" day here
// reads the same green as an "approved" pill.
const STATUS_COLORS = {
  PRESENT: { bg: '#E8F8EF', fg: '#1E9E5A' },
  HALF_DAY: { bg: '#FCF1DA', fg: '#B9791A' },
  ON_LEAVE: { bg: '#F1EAFB', fg: '#8B5CE0' },
  HOLIDAY: { bg: '#EAF1FE', fg: '#2F6FED' },
  ABSENT: { bg: '#FBEAE8', fg: '#D5372A' },
  PENDING_OUT: { bg: '#FCF1DA', fg: '#B9791A' },
};

/**
 * Two jobs in one component, since both are "look at one employee's attendance for a
 * month" and share the same calendar grid:
 *  - View mode (editable=false): click a staff name anywhere to see their real
 *    attendance/leave/holiday history for a month (getUserCalendar — admin can pass any
 *    user_id, see attendanceService.js).
 *  - Editable/backfill mode (editable=true): PayrollSlipTab opens this for a past month
 *    with no punch history. Every day starts however the backend already has it
 *    (usually ABSENT, since nothing was ever punched); "Mark All Present" flips every
 *    day to PRESENT, then the admin clicks individual real absence days to flip them
 *    back. Save writes the whole month in one call (bulkMarkAttendance) and hands
 *    control back to the caller via onSaved so it can generate the payslip immediately.
 */
const AttendanceDrawer = ({ show, userId, userName, month, year, editable, projectId, saveLabel, onClose, onSaved }) => {
  const [curMonth, setCurMonth] = useState(month);
  const [curYear, setCurYear] = useState(year);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCurMonth(month);
    setCurYear(year);
  }, [month, year, userId]);

  useEffect(() => {
    if (!show || !userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setOverrides({});
      try {
        const res = await getUserCalendar(userId, curMonth, curYear);
        if (!cancelled) setDays(res?.days || []);
      } catch (err) {
        console.error('Failed to load attendance calendar:', err);
        if (!cancelled) setDays([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [show, userId, curMonth, curYear]);

  const effectiveStatus = (day) => overrides[day.date] || day.status;

  const summary = useMemo(() => {
    const counts = { PRESENT: 0, ABSENT: 0, HALF_DAY: 0, ON_LEAVE: 0, HOLIDAY: 0 };
    days.forEach(d => {
      const s = effectiveStatus(d);
      if (counts[s] !== undefined) counts[s] += 1;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, overrides]);

  const leadingBlanks = days.length ? new Date(curYear, curMonth - 1, 1).getDay() : 0;

  const handleMarkAllPresent = () => {
    const next = {};
    days.forEach(d => { next[d.date] = 'PRESENT'; });
    setOverrides(next);
  };

  const handleDayClick = (day) => {
    if (!editable) return;
    const cur = effectiveStatus(day);
    setOverrides(prev => ({ ...prev, [day.date]: cur === 'ABSENT' ? 'PRESENT' : 'ABSENT' }));
  };

  const handleSave = async () => {
    const absentDates = days.filter(d => effectiveStatus(d) === 'ABSENT').map(d => d.date);
    setSaving(true);
    try {
      await bulkMarkAttendance(userId, curMonth, curYear, absentDates, projectId);
      toast.success(`Attendance filled for ${MONTHS[curMonth - 1]} ${curYear}.`);
      onSaved && onSaved();
    } catch (err) {
      console.error('Bulk attendance save failed:', err);
      toast.error('Failed to save attendance for this month.');
    } finally {
      setSaving(false);
    }
  };

  const shiftMonth = (delta) => {
    let m = curMonth + delta;
    let y = curYear;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setCurMonth(m);
    setCurYear(y);
  };

  if (!userId) return null;

  return (
    <>
      <S.DrawerOverlay show={show} onClick={onClose} />
      <S.DrawerContainer show={show}>
        <div className="drawer-head">
          <button className="close" onClick={onClose}>✕</button>
          <div className="drawer-name">{userName || 'Staff'}</div>
          <div className="drawer-role">
            {editable ? 'Fill past-month attendance for payroll' : 'Attendance history'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
            <S.Btn ghost sm onClick={() => shiftMonth(-1)}>‹</S.Btn>
            <strong>{MONTHS[curMonth - 1]} {curYear}</strong>
            <S.Btn ghost sm onClick={() => shiftMonth(1)}>›</S.Btn>
          </div>
        </div>

        <div className="drawer-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                {['PRESENT', 'ABSENT', 'ON_LEAVE', 'HALF_DAY'].map(s => (
                  <div key={s} style={{ background: STATUS_COLORS[s].bg, borderRadius: 8, padding: '6px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: STATUS_COLORS[s].fg }}>{summary[s] || 0}</div>
                    <div style={{ fontSize: 9.5, color: STATUS_COLORS[s].fg, fontWeight: 700 }}>{s.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>

              {editable && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 11.5, color: '#9297A3', marginBottom: 8 }}>
                    Saving here writes attendance for every day of {MONTHS[curMonth - 1]} {curYear} for {userName}.
                    Mark the whole month present, then click the specific days they were actually absent.
                  </p>
                  <S.Btn sm onClick={handleMarkAllPresent}>Mark All Present</S.Btn>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                {WEEKDAYS.map((w, i) => (
                  <div key={i} style={{ textAlign: 'center', fontSize: 10, color: '#9297A3', fontWeight: 700 }}>{w}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {Array.from({ length: leadingBlanks }).map((_, i) => <div key={`b${i}`} />)}
                {days.map(d => {
                  const s = effectiveStatus(d);
                  const colors = STATUS_COLORS[s] || { bg: '#EEF0F3', fg: '#4B4F5C' };
                  return (
                    <div
                      key={d.date}
                      onClick={() => handleDayClick(d)}
                      title={s.replace('_', ' ')}
                      style={{
                        background: colors.bg,
                        color: colors.fg,
                        borderRadius: 8,
                        padding: '8px 0',
                        textAlign: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: editable ? 'pointer' : 'default',
                      }}
                    >
                      {d.day}
                    </div>
                  );
                })}
              </div>

              {editable && (
                <div style={{ marginTop: 20 }}>
                  <S.Btn onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : (saveLabel || 'Save Attendance')}
                  </S.Btn>
                </div>
              )}
            </>
          )}
        </div>
      </S.DrawerContainer>
    </>
  );
};

export default AttendanceDrawer;
